import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, InputNumber, Button, message, Row, Col, Tooltip, Upload, Checkbox, Divider, Table, Modal, Tag, Space, Popconfirm } from 'antd';
import { ArrowLeftOutlined, InboxOutlined, InfoCircleOutlined, EyeOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createTask, getTasks, deleteTaskLocal, TaskResponse } from '../../utils/taskManager';
import { shouldDisplayInList } from '../../utils/taskManager';
import MolStarChainViewer from '../../components/results/protein/structure/MolStarChainViewer';
import ProdigyResultComponent from '../../components/results/interaction/ProdigyResult';

const { Title, Paragraph } = Typography;

type ProdigyTaskResult = {
  results?: Array<{
    binding_affinity?: number;
    dissociation_constant?: number;
    intermolecular_contacts?: number;
    charged_charged_contacts?: number;
    charged_polar_contacts?: number;
    charged_apolar_contacts?: number;
    polar_polar_contacts?: number;
    apolar_polar_contacts?: number;
    apolar_apolar_contacts?: number;
    apolar_nis_percentage?: number;
    command?: string;
  }>;
  structure_info?: any;
  runtime?: number;
};

export default function ProdigyAsyncPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [currentTaskResult, setCurrentTaskResult] = useState<ProdigyTaskResult | null>(null);
  const [resultModalVisible, setResultModalVisible] = useState<boolean>(false);
  const [pdbData, setPdbData] = useState<string | null>(null);
  const [structureInfo, setStructureInfo] = useState<any>(null);

  // 组件挂载时自动刷新任务列表，并设置定时轮询
  useEffect(() => {
    refreshTasks();
    const interval = setInterval(refreshTasks, 5000); // 每5秒自动刷新
    return () => clearInterval(interval);
  }, []);

  const handleFileChange = (info: any) => {
    if (info.file.status === 'removed') { 
      setFile(null); 
      setStructureInfo(null);
      setPdbData(null);
      return; 
    }
    const fileObj = info.file.originFileObj || (info.file.status !== 'uploading' ? info.file : null);
    if (fileObj instanceof File) {
      setFile(fileObj);
      
      // 读取文件内容
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          const content = event.target.result.toString();
          setPdbData(content);
          
          // 解析链信息
          try {
            const structInfo = parseStructureChains(content, fileObj.name);
            setStructureInfo(structInfo);
            if (structInfo.chains.length > 0) {
              message.success(`识别到${structInfo.chains.length}个链: ${structInfo.chains.join(', ')}`);
            }
          } catch (error) {
            message.error('解析结构文件失败');
            setStructureInfo(null);
          }
        }
      };
      reader.readAsText(fileObj);
    }
  };

  const uploadProps = { 
    name: 'file', 
    multiple: false, 
    maxCount: 1, 
    accept: '.pdb,.cif,.mmcif', 
    beforeUpload: () => false, 
    onChange: handleFileChange 
  };

  // 解析结构文件获取链信息
  const parseStructureChains = (content: string, filename: string) => {
    const chains: string[] = [];
    const lines = content.split('\n');
    const ext = filename.toLowerCase().split('.').pop();

    if (ext === 'pdb') {
      for (const line of lines) {
        if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
          const chainId = line.charAt(21).trim();
          if (chainId && !chains.includes(chainId)) {
            chains.push(chainId);
          }
        }
      }
    } else if (ext === 'cif' || ext === 'mmcif') {
      // CIF/mmCIF格式：loop_ 后跟列头定义（_atom_site.xxx），然后是数据行
      let inAtomSiteLoop = false;
      let dataStarted = false;
      let columnHeaders: string[] = [];
      let authAsymIdx: number | null = null;
      let labelAsymIdx: number | null = null;
      let groupPdbIdx: number | null = null;

      for (const raw of lines) {
        const stripped = raw.trim();

        if (stripped === 'loop_') {
          // 遇到新loop_表示atom_site块结束
          if (inAtomSiteLoop && dataStarted) break;
          inAtomSiteLoop = false;
          dataStarted = false;
          columnHeaders = [];
          authAsymIdx = null;
          labelAsymIdx = null;
          groupPdbIdx = null;
          continue;
        }

        // 检测 _atom_site 列头
        if (stripped.startsWith('_atom_site.')) {
          if (!inAtomSiteLoop) inAtomSiteLoop = true;
          columnHeaders.push(stripped);
          if (stripped === '_atom_site.auth_asym_id') authAsymIdx = columnHeaders.length - 1;
          else if (stripped === '_atom_site.label_asym_id') labelAsymIdx = columnHeaders.length - 1;
          else if (stripped === '_atom_site.group_PDB') groupPdbIdx = columnHeaders.length - 1;
          continue;
        }

        // 列头读完后的数据行
        if (inAtomSiteLoop && columnHeaders.length > 0 && !stripped.startsWith('_')) {
          if (stripped === '#' || stripped === '') {
            if (dataStarted) break;
            continue;
          }
          dataStarted = true;
          const chainColIdx = authAsymIdx !== null ? authAsymIdx : labelAsymIdx;
          if (chainColIdx !== null) {
            const parts = stripped.split(/\s+/);
            if (parts.length > chainColIdx) {
              const chainId = parts[chainColIdx].trim();
              // 只取 ATOM/HETATM 记录的链（如果有 group_PDB 列）
              if (groupPdbIdx !== null && parts.length > groupPdbIdx) {
                const recordType = parts[groupPdbIdx].trim();
                if (recordType !== 'ATOM' && recordType !== 'HETATM') continue;
              }
              if (chainId && !chains.includes(chainId)) {
                chains.push(chainId);
              }
            }
          }
          continue;
        }
      }
    }

    chains.sort();
    return { filename, chains, size: content.length };
  };

  const submitTask = async () => {
    if (!file) { 
      message.error('请上传结构文件'); 
      return; 
    }
    
    try {
      setSubmitting(true);
      setError(null);
      const values = await form.validateFields();

      // 读取文件内容
      const toText = (file: File) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const fileContent = await toText(file);
      
      let ligandChains = values.ligand_chains || [];
      let receptorChains = values.receptor_chains || [];
      if (!Array.isArray(ligandChains)) ligandChains = [ligandChains];
      if (!Array.isArray(receptorChains)) receptorChains = [receptorChains];

      if (ligandChains.length === 0) {
        message.error('请至少选择一个配体链');
        return;
      }
      if (receptorChains.length === 0) {
        message.error('请至少选择一个受体链');
        return;
      }

      const selectionsStr = `${ligandChains.join(',')} ${receptorChains.join(',')}`;

      const task = await createTask({
        name: 'PRODIGY分析',
        description: 'PRODIGY蛋白质-蛋白质互作分析',
        service_name: 'molecular_interaction',
        task_type: 'prodigy',
        handler_name: 'prodigy_analyze_json',
        parameters: {
          structure_content: fileContent,
          filename: file.name,
          temperature: values.temperature || 25.0,
          distance_cutoff: values.distance_cutoff || 5.5,
          selections: selectionsStr
        },
        priority: 5,
      });

      message.success(`任务已提交：${task.id}`);
      await refreshTasks();
    } catch (e: any) {
      setError(e?.message || '提交任务失败');
      message.error(`提交任务失败: ${e?.message || '未知错误'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const refreshTasks = async () => {
    try {
      const list = await getTasks(0, 50, undefined, 'molecular_interaction', 'prodigy');
      // 过滤掉前端已删除的任务（后端仍保留，供管理员管理）
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const filtered = list.filter(task => !deletedTaskIds.includes(task.id));
      setTasks(filtered);
    } catch (err) {
      console.error('获取任务列表失败:', err);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      // 本地删除：仅前端隐藏该任务，后端保留由管理员处理
      await deleteTaskLocal(taskId);
      // 立即从当前列表移除，提升响应速度
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setSelectedTaskIds(prev => prev.filter(id => id !== taskId));
      message.success('任务已删除');
    } catch (err) {
      console.error('本地删除任务失败:', err);
      message.error('删除任务失败');
    }
  };

  const handleViewResult = (task: TaskResponse) => {
    if (task.result) {
      setCurrentTaskResult(task.result as ProdigyTaskResult);
      setResultModalVisible(true);
    } else {
      message.warning('任务结果暂未生成');
    }
  };

  const renderStatus = (status: string) => {
    const statusMap: { [key: string]: { color: string; text: string } } = {
      'pending': { color: 'default', text: '等待中' },
      'queued': { color: 'blue', text: '队列中' },
      'running': { color: 'processing', text: '运行中' },
      'completed': { color: 'success', text: '已完成' },
      'failed': { color: 'error', text: '失败' },
      'cancelled': { color: 'default', text: '已取消' },
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: string) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{id.slice(0, 8)}</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatus,
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 80,
      render: (progress: number) => `${progress}%`,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 180,
      render: (time: string) => time ? new Date(time).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: TaskResponse) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewResult(record)}
            disabled={record.status !== 'completed'}
          >
            查看结果
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除这个任务吗？"
            onConfirm={() => handleDeleteTask(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/interaction')}
          style={{ marginBottom: 16 }}
        >
          返回分子互作分析
        </Button>
        
        <Typography>
          <Title level={2}>PRODIGY 蛋白质-蛋白质互作分析</Title>
          <Paragraph>
            PRODIGY是一个基于蛋白质复合物3D结构预测蛋白质-蛋白质结合亲和力的工具。
            请上传PDB或CIF/mmCIF格式的蛋白质结构文件进行分析。
          </Paragraph>
        </Typography>
      </div>

      <Card title="提交分析任务">
        <Form form={form} layout="vertical">
          <Form.Item
            name="structure_file"
            label="蛋白质结构文件"
            rules={[{ required: true, message: '请上传结构文件' }]}
          >
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">支持PDB、CIF、mmCIF格式文件</p>
            </Upload.Dragger>
          </Form.Item>

          {pdbData && structureInfo && (
            <Card title="结构预览" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={14}>
                  <div style={{ height: '400px' }}>
                    <Form.Item shouldUpdate={(prevValues, currentValues) => 
                      prevValues.ligand_chains !== currentValues.ligand_chains ||
                      prevValues.receptor_chains !== currentValues.receptor_chains
                    }>
                      {({ getFieldValue }) => (
                        <MolStarChainViewer
                          pdbData={pdbData}
                          height={400}
                          ligandChains={getFieldValue('ligand_chains') || []}
                          receptorChains={getFieldValue('receptor_chains') || []}
                        />
                      )}
                    </Form.Item>
                  </div>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name="temperature"
                    label={<span>温度 (℃) <Tooltip title="分析所使用的温度"><InfoCircleOutlined /></Tooltip></span>}
                    initialValue={25.0}
                  >
                    <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name="distance_cutoff"
                    label={<span>距离阈值 (Å) <Tooltip title="计算分子间接触的距离阈值"><InfoCircleOutlined /></Tooltip></span>}
                    initialValue={5.5}
                  >
                    <InputNumber min={3.0} max={10.0} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name="ligand_chains"
                    label={<span>配体链 <Tooltip title="选择作为配体的链"><InfoCircleOutlined /></Tooltip></span>}
                    rules={[{ required: true, message: '请选择至少一个配体链' }]}
                    initialValue={[]}
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      {structureInfo?.chains?.map((chain: string) => (
                        <Checkbox key={chain} value={chain}>{chain}</Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>

                  <Form.Item
                    name="receptor_chains"
                    label={<span>受体链 <Tooltip title="选择作为受体的链"><InfoCircleOutlined /></Tooltip></span>}
                    rules={[{ required: true, message: '请选择至少一个受体链' }]}
                    initialValue={[]}
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      {structureInfo?.chains?.map((chain: string) => (
                        <Checkbox key={chain} value={chain}>{chain}</Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>

                  <Form.Item>
                    <Button 
                      type="primary"
                      onClick={submitTask}
                      loading={submitting}
                      disabled={!file}
                      style={{ width: '100%' }}
                    >
                      提交分析
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}
        </Form>
      </Card>

      {/* 任务列表表格 */}
      <Card 
        title="任务历史" 
        style={{ marginTop: 16 }}
        extra={
          <Button 
            icon={<SyncOutlined />} 
            onClick={refreshTasks}
          >
            刷新任务列表
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 结果模态框 */}
      <Modal
        title="PRODIGY分析结果"
        open={resultModalVisible}
        onCancel={() => setResultModalVisible(false)}
        footer={null}
        width={1000}
      >
        {currentTaskResult && (
          <ProdigyResultComponent taskResult={currentTaskResult} />
        )}
      </Modal>
    </div>
  );
}
